import { useEffect, useState } from 'preact/hooks';
import { ApiError, request, type Session, describeError } from '../api/client';
import { newIdempotencyKey } from '../api/ids';
import type { WireCapabilities } from '../api/wire';
import { type ThemeChoice } from '../state/theme';
import type { Preferences } from '../state/preferences';

import { ToggleRow } from '../components/forms/ToggleRow.jsx';
import { ListRow } from '../components/navigation/ListRow.jsx';
import { Pressable } from '../components/a11y/Pressable';
import { SegmentedNav } from '../components/a11y/SegmentedNav';

/** Collections the server can turn on and off. Keys are the exact ones
 *  `put_collection_setting` accepts; anything else is a 422. */
const COLLECTIONS = [
  { key: 'habits', label: 'Habits', help: 'Trackers on the daily log.' },
  { key: 'reading_log', label: 'Reading Log', help: 'Books, with a rating.' },
  { key: 'gratitude_log', label: 'Gratitude Log', help: 'One entry per day.' },
  { key: 'commonplace_books', label: 'Commonplace Books', help: 'Quotes, lists and notes.' },
] as const;

interface CalendarConnection {
  provider: string;
  status: string;
  selected_calendar_name?: string;
}

export function Settings({
  session,
  theme,
  onTheme,
  preferences,
  onPreferences,
  onDismiss,
  onDisconnect,
  onToken,
}: {
  session: Session;
  theme: ThemeChoice;
  onTheme: (next: ThemeChoice) => void;
  preferences: Preferences;
  onPreferences: (next: Preferences) => void;
  onDismiss: () => void;
  onDisconnect: () => void;
  /** Supplying the credential a `Continue offline` session started without. */
  onToken: (token: string) => void;
}) {
  const [collections, setCollections] = useState<Record<string, boolean> | null>(null);
  const [calendars, setCalendars] = useState<CalendarConnection[] | null>(null);
  const [capabilities, setCapabilities] = useState<WireCapabilities | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Disconnecting drops the only copy of the token on this device. The brief's
  // rule is that irreversible-feeling actions carry more friction than
  // completing a task, so it takes a second, deliberate tap.
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [pendingToken, setPendingToken] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const caps = (await request<WireCapabilities>(session, '/v1/capabilities')).body;
        if (cancelled) return;
        setCapabilities(caps);
        setCollections(caps.collections);
      } catch (caught) {
        if (!cancelled) setError(describe(caught));
      }
      try {
        const status = (
          await request<{ connections: Record<string, CalendarConnection> }>(
            session,
            '/v1/calendar/connection',
          )
        ).body;
        if (!cancelled) setCalendars(Object.values(status.connections ?? {}));
      } catch {
        // No calendar configured answers 409 or 503. That is a state, not a
        // failure, and the section says so rather than disappearing.
        if (!cancelled) setCalendars([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  async function toggle(key: string, next: boolean) {
    // Optimistic, then corrected from the response — the server returns the
    // whole settings object, so it is the authority on what actually changed.
    setCollections((current) => (current ? { ...current, [key]: next } : current));
    try {
      const updated = (
        await request<Record<string, boolean>>(session, `/v1/settings/collections/${key}`, {
          method: 'PUT',
          idempotencyKey: newIdempotencyKey(),
          body: { enabled: next },
        })
      ).body;
      setCollections(updated);
      setError(null);
    } catch (caught) {
      setCollections((current) => (current ? { ...current, [key]: !next } : current));
      setError(describe(caught));
    }
  }

  return (
    <div style={sheet}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={eyebrow}>BUJO / INDEX</span>
        <span style={label}>SETTINGS</span>
        <button type="button" onClick={onDismiss} style={{ ...label, color: 'var(--bujo-text-meta)', minHeight: 'var(--bujo-row-min)', cursor: 'pointer' }}>
          CLOSE
        </button>
      </header>

      {error ? <Line tone="error">! {error}</Line> : null}

      <section>
        <span style={label}>COLLECTIONS</span>
        <Line>Disabling one hides it. Nothing is deleted.</Line>
        {collections
          ? COLLECTIONS.map((c) => (
              <ToggleRow
                key={c.key}
                label={c.label}
                help={c.help}
                value={collections[c.key] ?? false}
                onChange={(next: boolean) => void toggle(c.key, next)}
              />
            ))
          : <Line>Loading…</Line>}
      </section>

      <section>
        <span style={label}>APPEARANCE</span>
        {/* Three states shown at once, as the reference draws them. Tap-to-cycle
            made the current choice guessable only by reading the label, and
            reaching `light` from `dark` took two taps through `system`. */}
        <div style={themeRow}>
          <span style={themeRowLabel}>Theme</span>
          <SegmentedNav
            label="Theme"
            variant="boxed"
            active={theme}
            onSelect={onTheme}
            options={[
              { value: 'system', label: 'SYSTEM' },
              { value: 'dark', label: 'DARK' },
              { value: 'light', label: 'LIGHT' },
            ]}
          />
        </div>
        <ToggleRow
          label="Show day-of-year"
          help="DAY 214 beside the date, as the terminal client shows it."
          value={preferences.showDayOfYear}
          onChange={(next: boolean) => onPreferences({ ...preferences, showDayOfYear: next })}
        />
      </section>

      <section>
        <span style={label}>JOURNAL</span>
        <ToggleRow
          label="Week starts Monday"
          help="Ranges are inclusive, and there is no “this week” rollup — a task belongs to its day."
          value={preferences.weekStart === 'monday'}
          onChange={(next: boolean) =>
            onPreferences({ ...preferences, weekStart: next ? 'monday' : 'sunday' })
          }
        />
      </section>

      <section>
        <span style={label}>CALENDARS</span>
        {calendars === null ? (
          <Line>Loading…</Line>
        ) : calendars.length === 0 ? (
          <Line>No provider configured.</Line>
        ) : (
          calendars.map((c) => (
            <ListRow
              key={c.provider}
              label={c.provider}
              meta={`${c.selected_calendar_name ?? c.status} · read-only`}
              dim={c.status !== 'connected'}
            />
          ))
        )}
        <Line>
          BuJo reads calendars and never writes to them. There is no RSVP, edit or delete
          anywhere in the product — change an event where it lives.
        </Line>
      </section>

      <section>
        <span style={label}>SERVER</span>
        <ListRow label="URL" meta={session.baseUrl || 'same origin'} />
        <ListRow
          label="Token"
          meta={session.token.trim() ? 'stored on this device' : 'not set'}
        />
        <Line>Never synced, never in a backup.</Line>
        {session.token.trim() ? null : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = pendingToken.trim();
              if (trimmed) onToken(trimmed);
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bujo-space-2)' }}
          >
            <Line>
              Captures are queued on this device. Adding a token starts reading the journal and
              sends them.
            </Line>
            <input
              value={pendingToken}
              onInput={(e) => setPendingToken((e.target as HTMLInputElement).value)}
              placeholder="paste the scoped token"
              autocomplete="off"
              spellcheck={false}
              autocapitalize="none"
              autocorrect="off"
              style={tokenInput}
            />
            <button type="submit" disabled={!pendingToken.trim()} style={tokenButton(Boolean(pendingToken.trim()))}>
              CONNECT
            </button>
          </form>
        )}
        {capabilities ? (
          <>
            <ListRow label="Contract" meta={capabilities.contract_version} />
            <ListRow label="Schema" meta={String(capabilities.schema_version)} />
            <ListRow label="Journal timezone" meta={capabilities.journal_timezone} />
          </>
        ) : (
          <Line>Loading…</Line>
        )}
        <Pressable
          label={
            confirmingDisconnect
              ? 'Confirm disconnect: forget this server and its token'
              : 'Disconnect from this server'
          }
          onPress={() => {
            if (confirmingDisconnect) onDisconnect();
            else setConfirmingDisconnect(true);
          }}
        >
          <ListRow
            label={confirmingDisconnect ? 'Tap again to disconnect' : 'Disconnect'}
            meta={confirmingDisconnect ? 'forgets the token' : ''}
            dim={!confirmingDisconnect}
          />
        </Pressable>
      </section>
    </div>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return 'This token cannot change settings — it needs the settings:write scope.';
    }
    return describeError(error);
  }
  return String(error);
}

function Line({ children, tone }: { children: unknown; tone?: 'error' }) {
  return (
    <p
      style={{
        margin: '0 0 var(--bujo-space-2)',
        font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-row) var(--bujo-font-sans)',
        color: tone === 'error' ? 'var(--bujo-state-error)' : 'var(--bujo-text-meta)',
      }}
    >
      {children as never}
    </p>
  );
}

const sheet = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 40,
  background: 'var(--bujo-surface-page)',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'var(--bujo-space-5)',
  padding: 'var(--bujo-gutter)',
  paddingTop: 'calc(var(--bujo-gutter) + env(safe-area-inset-top))',
  paddingLeft: 'calc(var(--bujo-gutter) + env(safe-area-inset-left))',
  paddingRight: 'calc(var(--bujo-gutter) + env(safe-area-inset-right))',
  paddingBottom: 'calc(var(--bujo-gutter) + env(safe-area-inset-bottom))',
  overflowY: 'auto' as const,
};

const themeRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--bujo-space-3)',
  flexWrap: 'wrap' as const,
  minHeight: 'var(--bujo-tap-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
  paddingBottom: 'var(--bujo-space-2)',
};
const themeRowLabel = {
  flex: 'none',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-primary)',
};

const eyebrow = {
  display: 'block',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};

const label = {
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-label)',
};

const tokenInput = {
  minHeight: 'var(--bujo-row-min)',
  background: 'var(--bujo-surface-sheet)',
  border: '1px solid var(--bujo-rule-hairline)',
  borderRadius: 0,
  padding: 'var(--bujo-space-2)',
  color: 'var(--bujo-text-primary)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)',
};
const tokenButton = (enabled: boolean) => ({
  minHeight: 'var(--bujo-tap-min)',
  background: 'none',
  border: `1px solid ${enabled ? 'var(--bujo-accent)' : 'var(--bujo-rule-hairline)'}`,
  color: enabled ? 'var(--bujo-accent)' : 'var(--bujo-text-faint)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  cursor: enabled ? 'pointer' : 'default',
});
